<template>
  <div
    class="px-10 pt-3 pb-20 md:px-24 md:pb-10"
  >
    <settings-header
      active-tab="filters"
      :entity-id="entity.id"
    />
    <div class="flex justify-between mt-10">
      <h1
        class="text-xl text-left text-sc-gray-dark"
      >
        {{ $t('message.settings.filtersTab.defaultFilters') }}
      </h1>
      <button
        class="inline-block px-3 py-1 text-sm leading-5 text-center text-white rounded bg-sc-blue hover:text-white"
        @click="defaultFiltersModal"
      >
        {{ $t('message.settings.filtersTab.addFilter') }}
      </button>
    </div>
    <div class="mt-5">
      <b-table
        :data="defaultFilters"
        class="border-t border-b border-sc-gray-light"
        :row-class="() => 'group'"
        hoverable
      >
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="name"
          :label="columnFormatter($t('message.settings.filtersTab.table.header.name'))"
          v-slot="props"
          cell-class="border-0"
        >
          <p
            class="text-sm text-primary"
          >
            {{ props.row.name }}
          </p>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="filters"
          :label="columnFormatter($t('message.settings.filtersTab.table.header.filters'))"
          v-slot="props"
          cell-class="border-0"
        >
          <p
            class="text-sm text-primary"
          >
            {{ createFilterSummary(props.row) }}
          </p>
        </b-table-column>
        <b-table-column
          field="remove"
          v-slot="props"
        >
          <button
            class="visible px-2 text-xs leading-6 bg-white rounded md:invisible shadow-sc-button group-hover:visible text-primary"
            slot="trigger"
            @click="removeConfirmationModal(props.row)"
          >
            {{ $t('message.settings.membersTab.table.removeButton') }}
          </button>
        </b-table-column>
      </b-table>
    </div>
    <default-filters-modal
      ref="defaultFiltersModal"
      :entity-id="entity.id"
      :accounts="entityAccounts"
      @add-default-filter="addDefaultFilter"
    />
    <confirmation-modal
      ref="removeConfirmationModal"
      :content="`¿Está seguro que desea eliminar el filtro: ${filterToRemove.name},
                de esta supercartola?`"
      flip-buttons-style
      :is-approval-modal="false"
      @confirmation="removeDefaultFilter"
    />
  </div>
</template>

<script>
import { camelize } from 'humps';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import columnFormatter from '@/helpers/columnFormatter';
import defaultFiltersApi from '@/src/api/default_filters';
import SettingsHeader from './settings-header.vue';
import DefaultFiltersModal from './default-filters-modal.vue';
import ConfirmationModal from './confirmation-modal.vue';

export default {
  data() {
    return {
      institutions: {
        'all': 'Todos los Bancos',
        'cl_banco_de_chile': this.$t('message.accounts.institution.clBancoDeChile'),
        'cl_banco_santander': this.$t('message.accounts.institution.clBancoSantander'),
        'cl_banco_bci': this.$t('message.accounts.institution.clBancoBci'),
        'cl_banco_estado': this.$t('message.accounts.institution.clBancoEstado'),
        'cl_banco_itaú': this.$t('message.accounts.institution.clBancoItau'),
      },
      status: {
        'pending': this.$t('message.table.status.pending'),
        'approved': this.$t('message.table.status.approved'),
      },
      movementTypes: {
        // eslint-disable-next-line no-magic-numbers
        'all': this.$tc('message.table.header.all', 2),
        'transfer': this.$t('message.table.header.transferences'),
        'other': this.$t('message.table.header.others'),
      },
      accounts: [],
      currentDefaultFilters: [],
      filterToRemove: {},
      accountsShownDigits: -4,
    };
  },
  components: {
    SettingsHeader,
    DefaultFiltersModal,
    ConfirmationModal,
  },
  props: {
    entity: {
      type: Object,
      required: true,
    },
    entityDefaultFilters: {
      type: Array,
      required: true,
    },
    entityAccounts: {
      type: Array,
      required: true,
    },
  },
  computed: {
    defaultFilters() {
      return this.currentDefaultFilters;
    },
  },
  methods: {
    // eslint-disable-next-line max-statements
    createFilterSummary(defaultFilter) {
      const filters = [];
      if (defaultFilter.type) filters.push(`Movimientos: ${this.movementTypes[defaultFilter.type]}`);
      if (defaultFilter.status) filters.push(`Estados: ${this.status[defaultFilter.status]}`);
      if (defaultFilter.fromAmount) filters.push(`Monto > ${defaultFilter.fromAmount}`);
      if (defaultFilter.toAmount) filters.push(`Monto < ${defaultFilter.toAmount}`);
      if (defaultFilter.inbound === false) filters.push('Tipo de movimiento: Solo egresos');
      if (defaultFilter.outbound === false) filters.push('Tipo de movimiento: Solo ingresos');
      if (defaultFilter.description) filters.push(`Descripción: ${defaultFilter.description}`);
      if (defaultFilter.institution) filters.push(`Banco origen: ${this.institutions[defaultFilter.institution]}`);
      if (defaultFilter.postDate.length > 0) {
        filters.push(
          `Desde el: ${this.formatDate(defaultFilter.postDate[0])}
           Hasta el: ${this.formatDate(defaultFilter.postDate[1])}`);
      }
      if (defaultFilter.accounts.length > 0) {
        filters.push(
          defaultFilter.accounts[0] ? `Solo cuentas: ${this.formatAccounts(defaultFilter.accounts)}` : 'Ninguna cuenta',
        );
      }

      return filters.join(' , ');
    },
    formatDate(dateString) {
      const newDate = new Date(dateString);
      const day = newDate.getDate();
      const month = newDate.getMonth() + 1;
      const year = newDate.getFullYear();

      return `${day}-${month}-${year}`;
    },

    formatAccounts(accounts) {
      return accounts.map((accountId) => this.formatAccount(accountId)).join(' , ');
    },
    formatAccount(accountId) {
      const account = this.accounts[accountId];

      return `[${this.$t(`message.accounts.institution.shortNames.${camelize(account.institution)}`)}]
              ${account.customName ? account.customName : this.$t(`message.accounts.type.${camelize(account.type)}`)}
              - XX${account.number.slice(this.accountsShownDigits)}`;
    },
    columnFormatter,
    loadCurrentDefaultFilters() {
      this.currentDefaultFilters = this.entityDefaultFilters;
    },
    loadAccounts() {
      this.accounts = mapValues(
        keyBy(this.entityAccounts, 'id'),
        (account) => ({
          customName: account.customName,
          number: account.number,
          type: account.accountType,
          institution: account.institution,
        }),
      );
    },
    defaultFiltersModal() {
      this.$refs.defaultFiltersModal.show();
    },
    async addDefaultFilter(filterParams) {
      const { data: { defaultFilter } } = await defaultFiltersApi.createFilter(
        this.entity.id,
        filterParams,
      );
      this.currentDefaultFilters.push(defaultFilter);
    },
    removeConfirmationModal(filter) {
      this.filterToRemove = filter;
      this.$refs.removeConfirmationModal.show();
    },

    async removeDefaultFilter() {
      await defaultFiltersApi.removeFilter(
        this.entity.id,
        this.filterToRemove.id,
      );
      this.currentDefaultFilters.splice(this.currentDefaultFilters.indexOf(this.filterToRemove), 1);
    },
  },
  beforeMount() {
    this.loadCurrentDefaultFilters();
    this.loadAccounts();
  },
};
</script>
